import { useEffect, useMemo, useRef } from 'react';
import type { Round } from '@h2h/shared';
import type { SeasonRoundResult } from '../../hooks/useQueries';
import { cn } from '../../lib/cn';

/** Compact match-day label, e.g. "Sat 14 Jun" (kept local to avoid a screens import). */
function shortDay(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** One selectable round in the date pager. `isLive` = the current (unsettled) round. */
export type RoundDateTab = { roundId: string; label: string; isLive: boolean; sortKey: number };

/**
 * The shared "how you ended each date" tab model used by the League Hub division
 * card and the Standings Division view, so both read identically: past rounds you
 * entered run oldest → newest, with "Now" (the live round) pinned LAST (rightmost).
 *
 * Groups reshuffle every round (the ladder resets), so each tab is a *different*
 * set of rivals — a history of head-to-head battles, not one division evolving.
 */
export function useRoundDateTabs({
  currentRound,
  seasonResults,
  isCycling
}: {
  currentRound: Round | null | undefined;
  seasonResults: SeasonRoundResult[] | undefined;
  isCycling: boolean;
}): RoundDateTab[] {
  const currentRoundId = currentRound?.roundId ?? null;
  const currentLive = currentRound != null && currentRound.status !== 'SETTLED';
  return useMemo(() => {
    const out: RoundDateTab[] = [];
    const seen = new Set<string>();
    if (currentLive && currentRoundId) {
      out.push({ roundId: currentRoundId, label: 'Now', isLive: true, sortKey: Number.POSITIVE_INFINITY });
      seen.add(currentRoundId);
    }
    for (const r of seasonResults ?? []) {
      if (seen.has(r.round.roundId)) continue;
      seen.add(r.round.roundId);
      out.push({
        roundId: r.round.roundId,
        label: isCycling ? r.round.label : shortDay(r.round.firstKickoffAt),
        isLive: false,
        sortKey: r.round.firstKickoffAt
      });
    }
    return out.sort((a, b) => a.sortKey - b.sortKey);
  }, [seasonResults, currentLive, currentRoundId, isCycling]);
}

/**
 * Which tab to land on before the player taps. Normally the newest (rightmost) —
 * "Now" when live. But the live board has no scores until its first fixture
 * settles, so an opener landing on "Now" sees an empty "No scores yet" board.
 * Until then we default to the latest SETTLED round (the tab just before "Now"),
 * which actually has scores — "Now" stays one tap away.
 */
export function defaultRoundDateTabId(
  tabs: RoundDateTab[],
  liveProgress: { settled: number; total: number } | null | undefined
): string | null {
  const last = tabs[tabs.length - 1];
  if (!last) return null;
  const liveHasNoScores = last.isLive && !!liveProgress && liveProgress.settled === 0;
  if (liveHasNoScores && tabs.length > 1) return tabs[tabs.length - 2]!.roundId;
  return last.roundId;
}

/** Horizontal date strip — past round dates oldest → newest, "Now" pinned last.
 *  Auto-scrolls to the end on load so the newest tab is in view. */
export function RoundDatePager({
  tabs,
  selectedId,
  onSelect,
  className
}: {
  tabs: RoundDateTab[];
  selectedId: string | null;
  onSelect: (roundId: string) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [tabs.length]);

  return (
    <div
      ref={ref}
      className={cn(
        'flex gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {tabs.map((t) => {
        const active = t.roundId === selectedId;
        return (
          <button
            key={t.roundId}
            type="button"
            onClick={() => onSelect(t.roundId)}
            className={cn(
              'tap flex shrink-0 items-center gap-[5px] rounded-[11px] px-3 py-[7px] text-[12.5px] tracking-snug',
              active ? 'bg-night font-bold text-white' : 'bg-fill font-semibold text-ink2'
            )}
          >
            {t.isLive && (
              <span className={cn('h-[5px] w-[5px] rounded-full', active ? 'animate-h2h-pulse bg-accent' : 'bg-accent')} />
            )}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
